/**
 * Sales Qualification Service
 * Maps comprehensive enterprise qualification data to pricing_assessments table
 * Uses JSONB fields for storing detailed enterprise data
 */

import { montanaHOAFormService } from '../hoa/montanaHOAFormService';
import { SalesQualificationData } from '../../../types/onboarding/salesQualification';

export interface SalesQualificationResult {
  success: boolean;
  assessmentId?: string;
  message: string;
  errors?: string[];
}

class SalesQualificationService {
  /**
   * Submit comprehensive enterprise qualification data
   * Maps to pricing_assessments table using JSONB storage for detailed data
   */
  async submitQualification(
    qualificationData: SalesQualificationData,
    selectedTier: 'silver' | 'gold' | 'custom' = 'custom',
    selectedAddons: string[] = [],
  ): Promise<SalesQualificationResult> {
    // ALWAYS use Montana HOA form service (PostgreSQL DROPLET)
    // Supabase fallback removed as per requirements
    console.log(
      '🏔️ Using Montana HOA form service for enterprise qualification (PostgreSQL DROPLET)',
    );

    const hoaPricingData = {
      selected_tier: selectedTier,
      community_name: qualificationData.communityName || '',
      total_units: qualificationData.organizationSize || '',
      community_type: qualificationData.communityType || '',
      contact_name: qualificationData.contactName || '',
      contact_email: qualificationData.contactEmail || '',
      contact_phone: qualificationData.contactPhone,
      selected_addons: selectedAddons,
      budget_range: qualificationData.budgetRange,
      urgency: qualificationData.urgencyLevel,
      decision_process: qualificationData.decisionTimeline,
      wishlist_features: { critical: qualificationData.criticalFeatures || [] },
      goals: { primary: qualificationData.topPriority || '' },
      success_metrics: { metrics: qualificationData.successMetrics || [] },
      source: 'enterprise-qualification',
      referrer: typeof document !== 'undefined' ? document.referrer : undefined,
    };

    const response = await montanaHOAFormService.submitPricingAssessment(hoaPricingData);

    if (!response.success) {
      return {
        success: false,
        message: response.message || 'Failed to submit enterprise qualification to PostgreSQL',
        errors: [response.error || 'Unknown error'],
      };
    }

    // Persist stakeholders if present
    if (
      qualificationData.decisionMakers &&
      qualificationData.decisionMakers.length &&
      response.data?.id
    ) {
      for (const stakeholder of qualificationData.decisionMakers) {
        await montanaHOAFormService.submitStakeholderContact({
          assessment_id: response.data.id as string,
          name: stakeholder.name,
          email: stakeholder.email,
          title: stakeholder.title,
          role: stakeholder.role,
        });
      }
    }

    return {
      success: true,
      assessmentId: response.data?.id as string,
      message: 'Enterprise qualification submitted successfully to PostgreSQL',
    };
  }

  /**
   * Extract Step 1: Community Profile data
   */
  private extractStep1Data(data: SalesQualificationData) {
    return {
      communityName: data.communityName,
      communityType: data.communityType,
      organizationSize: data.organizationSize,
      establishedYear: data.establishedYear,
      contact: {
        name: data.contactName,
        email: data.contactEmail,
        phone: data.contactPhone,
        title: data.contactTitle,
      },
      location: {
        state: data.state,
        city: data.city,
        zipCode: data.zipCode,
      },
      revenue: {
        annualRevenue: data.annualRevenue,
        budgetRange: data.budgetRange,
      },
    };
  }

  /**
   * Extract Step 2: Financial Assessment data
   */
  private extractStep2Data(data: SalesQualificationData) {
    return {
      treasury: {
        treasuryAmount: data.treasuryAmount,
        monthlyBudget: data.monthlyBudget,
        annualBudgetDetailed: data.annualBudgetDetailed,
        reserveFunds: data.reserveFunds,
        outstandingDebts: data.outstandingDebts,
        investmentAccounts: data.investmentAccounts,
      },
      assets: {
        buildingsOwned: data.buildingsOwned,
        infrastructureOwned: data.infrastructureOwned,
        landOwnership: data.landOwnership,
        sharedFacilities: data.sharedFacilities,
        equipmentOwned: data.equipmentOwned,
      },
      banking: {
        bankingRelationships: data.bankingRelationships,
        currentPlatforms: data.currentPlatforms,
      },
      challenges: {
        financialPainPoints: data.financialPainPoints,
        budgetApprovalProcess: data.budgetApprovalProcess,
      },
    };
  }

  /**
   * Extract Step 3: Current Challenges data
   */
  private extractStep3Data(data: SalesQualificationData) {
    return {
      challenges: {
        primary: data.primaryChallenges,
        communication: data.communicationChallenges,
        operational: data.operationalChallenges,
        compliance: data.complianceChallenges,
        security: data.securityChallenges,
      },
      priority: {
        priorityLevel: data.priorityLevel,
        topPriority: data.topPriority,
        challengeImpact: data.challengeImpact,
        solutionGaps: data.solutionGaps,
        urgencyLevel: data.urgencyLevel,
      },
      impact: {
        budgetImpacts: data.budgetImpacts,
      },
    };
  }

  /**
   * Extract Step 4: Technology Requirements data
   */
  private extractStep4Data(data: SalesQualificationData) {
    return {
      integration: {
        integrationNeeds: data.integrationNeeds,
        customizationLevel: data.customizationLevel,
        technicalComplexity: data.technicalComplexity,
      },
      timeline: {
        timelineUrgency: data.timelineUrgency,
      },
      currentTech: {
        currentTechStack: data.currentTechStack,
        currentTechStackDetails: data.currentTechStackDetails,
      },
    };
  }

  /**
   * Extract Step 5: Stakeholder Mapping data
   */
  private extractStep5Data(data: SalesQualificationData) {
    return {
      decisionMakers: data.decisionMakers,
      process: {
        decisionTimeline: data.decisionTimeline,
        budgetApproved: data.budgetApproved,
        competitiveEvaluation: data.competitiveEvaluation,
        decisionCriteria: data.decisionCriteria,
      },
    };
  }

  /**
   * Extract Step 6: Implementation Readiness data
   */
  private extractStep6Data(data: SalesQualificationData) {
    return {
      capacity: {
        implementationCapacity: data.implementationCapacity,
        changeManagementApproach: data.changeManagementApproach,
        internalResources: data.internalResources,
        changeExperience: data.changeExperience,
      },
      training: {
        trainingNeeds: data.trainingNeeds,
        preferredTrainingFormat: data.preferredTrainingFormat,
      },
      support: {
        supportRequirements: data.supportRequirements,
      },
      constraints: {
        implementationConstraints: data.implementationConstraints,
        implementationConcerns: data.implementationConcerns,
      },
      success: {
        customSuccessMetrics: data.customSuccessMetrics,
        riskMitigation: data.riskMitigation,
      },
    };
  }

  /**
   * Extract Step 7: Vision & Goals data
   */
  private extractStep7Data(data: SalesQualificationData) {
    return {
      use_case: {
        primaryUseCase: data.primaryUseCase,
        expectedUsers: data.expectedUsers,
        criticalFeatures: data.criticalFeatures,
        problemStatement: data.problemStatement,
        desiredOutcome: data.desiredOutcome,
        primaryUserGroups: data.primaryUserGroups,
      },
      success: {
        successMetrics: data.successMetrics,
        customMetrics: data.customMetrics,
      },
      vision: {
        longTermVision: data.longTermVision,
        strategicGoals: data.strategicGoals,
      },
      growth: {
        technologyRoadmap: data.technologyRoadmap,
        growthPattern: data.growthPattern,
        growthAreas: data.growthAreas,
        futureTrends: data.futureTrends,
      },
    };
  }

  /**
   * Extract Step 8: Enterprise Requirements data
   */
  private extractStep8Data(data: SalesQualificationData) {
    return {
      compliance: {
        complianceRequirements: data.complianceRequirements,
        customCompliance: data.customCompliance,
      },
      security: {
        securityRequirements: data.securityRequirements,
        securityConcerns: data.securityConcerns,
      },
      performance: {
        scalabilityNeeds: data.scalabilityNeeds,
        performanceRequirements: data.performanceRequirements,
      },
      integration: {
        integrationComplexity: data.integrationComplexity,
        integrationCapabilities: data.integrationCapabilities,
      },
      deployment: {
        deploymentModel: data.deploymentModel,
        geographicRequirements: data.geographicRequirements,
        infrastructureCapabilities: data.infrastructureCapabilities,
      },
      customization: {
        customDevelopmentNeeds: data.customDevelopmentNeeds,
        specializedFeatures: data.specializedFeatures,
        futureEnhancements: data.futureEnhancements,
        developmentPreferences: data.developmentPreferences,
      },
      priorities: {
        enterprisePriorities: data.enterprisePriorities,
      },
    };
  }

  /**
   * Map goals data for existing schema with enterprise distinction
   */
  private mapGoalsData(data: SalesQualificationData): unknown[] {
    const goals = [];

    // Mark all goals as enterprise-sourced to distinguish from Silver/Gold
    if (data.longTermVision) goals.push({ type: 'enterprise-vision', value: data.longTermVision });
    if (data.strategicGoals)
      goals.push({ type: 'enterprise-strategic', value: data.strategicGoals });
    if (data.primaryUseCase) goals.push({ type: 'enterprise-useCase', value: data.primaryUseCase });
    if (data.desiredOutcome) goals.push({ type: 'enterprise-outcome', value: data.desiredOutcome });

    return goals;
  }

  /**
   * Map stakeholder data for existing schema with enterprise distinction
   */
  private mapStakeholderData(data: SalesQualificationData): unknown[] {
    if (data.decisionMakers && data.decisionMakers.length > 0) {
      return data.decisionMakers.map((dm) => ({
        type: 'enterprise-stakeholder', // Mark as enterprise-sourced
        name: dm.name,
        title: dm.title,
        email: dm.email,
        influence: dm.influence,
        role: dm.role,
      }));
    }
    return [];
  }

  /**
   * Map wishlist features from various enterprise requirements
   */
  private mapWishlistFeatures(data: SalesQualificationData): unknown[] {
    const features = [];

    // All features marked as enterprise to distinguish from Silver/Gold simple features
    if (data.criticalFeatures) {
      features.push(
        ...data.criticalFeatures.map((f) => ({ type: 'enterprise-critical', feature: f })),
      );
    }
    if (data.specializedFeatures) {
      features.push({ type: 'enterprise-specialized', feature: data.specializedFeatures });
    }
    if (data.futureEnhancements) {
      features.push({ type: 'enterprise-future', feature: data.futureEnhancements });
    }

    return features;
  }

  /**
   * Map interests from various data points with enterprise distinction
   */
  private mapInterestsData(data: SalesQualificationData): unknown[] {
    const interests = [];

    // All interests marked as enterprise to distinguish from Silver/Gold simple interests
    if (data.complianceRequirements) {
      interests.push(
        ...data.complianceRequirements.map((c) => ({ type: 'enterprise-compliance', interest: c })),
      );
    }
    if (data.securityRequirements) {
      interests.push(
        ...data.securityRequirements.map((s) => ({ type: 'enterprise-security', interest: s })),
      );
    }
    if (data.growthAreas) {
      interests.push(...data.growthAreas.map((g) => ({ type: 'enterprise-growth', interest: g })));
    }

    return interests;
  }

  /**
   * Validate qualification data completeness
   */
  private validateQualificationData(data: SalesQualificationData) {
    const validation = {
      isComplete: true,
      missingRequired: [] as string[],
      warnings: [] as string[],
    };

    // Check required fields
    if (!data.communityName) validation.missingRequired.push('communityName');
    if (!data.contactEmail) validation.missingRequired.push('contactEmail');
    if (!data.communityType) validation.missingRequired.push('communityType');

    // Check for high-value data points
    if (!data.treasuryAmount) validation.warnings.push('Financial assessment incomplete');
    if (!data.decisionMakers?.length) validation.warnings.push('No decision makers identified');
    if (!data.primaryChallenges?.length) validation.warnings.push('No challenges identified');

    validation.isComplete = validation.missingRequired.length === 0;

    return validation;
  }

  /**
   * Count total available fields in qualification
   */
  private countTotalFields(data: SalesQualificationData): number {
    return Object.keys(data).length;
  }

  /**
   * Count completed fields in qualification
   */
  private countCompletedFields(data: SalesQualificationData): number {
    return Object.values(data).filter(
      (value) =>
        value !== null &&
        value !== undefined &&
        value !== '' &&
        (Array.isArray(value) ? value.length > 0 : true),
    ).length;
  }

  /**
   * Calculate completion percentage
   */
  private calculateCompletionPercentage(data: SalesQualificationData): number {
    const total = this.countTotalFields(data);
    const completed = this.countCompletedFields(data);
    return total > 0 ? Math.round((completed / total) * 100) : 0;
  }

  /**
   * Calculate estimated value based on qualification data
   */
  private calculateEstimatedValue(data: SalesQualificationData): number {
    let baseValue = 50000; // Base enterprise value

    // Size multiplier
    if (data.organizationSize && data.organizationSize.includes('1000+')) baseValue *= 3;
    else if (data.organizationSize && data.organizationSize.includes('500')) baseValue *= 2;

    // Treasury multiplier
    if (data.treasuryAmount && typeof data.treasuryAmount === 'number') {
      if (data.treasuryAmount > 1000000) baseValue *= 2;
    }

    // Complexity multiplier
    if (data.technicalComplexity === 'high') baseValue *= 1.5;
    if (data.customizationLevel === 'extensive') baseValue *= 1.3;

    return Math.round(baseValue);
  }

  /**
   * Create compatibility mapping for existing assessment processing
   * This ensures our enterprise data doesn't break Silver/Gold processing
   */
  private createCompatibilityMapping(data: SalesQualificationData) {
    // Map our comprehensive data to the basic assessment structure
    // that Silver/Gold processing might expect
    return {
      communityName: data.communityName,
      contactEmail: data.contactEmail,
      contactName: data.contactName,
      selectedTier: 'custom', // Always custom for enterprise
      totalUnits: data.organizationSize,
      communityType: data.communityType,
      // Map complex arrays to simple arrays for compatibility
      biggestChallenges: data.primaryChallenges || [],
      primaryGoals: data.strategicGoals ? [data.strategicGoals] : [],
      // Financial position simplified
      financialPosition: {
        treasuryAmount: data.treasuryAmount,
        annualBudget: data.annualBudgetDetailed,
      },
      // Technology assessment simplified
      technologyAssessment: {
        currentSoftware: data.currentTechStack || [],
        communicationMethods: [],
      },
    };
  }

  /**
   * Generate unique submission ID with enterprise prefix
   */
  private generateSubmissionId(): string {
    // Use ENTERPRISE prefix to distinguish from Silver/Gold submissions
    // Silver/Gold use "PRC-" prefix, so we use "ENT-" to avoid conflicts
    return `ENT-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Get client IP (placeholder - implement based on your setup)
   */
  private async getClientIP(): Promise<string | null> {
    // Implement IP detection based on your infrastructure
    return null;
  }
}

export const salesQualificationService = new SalesQualificationService();
